import { useMemo, useRef, useState, useEffect, type ReactNode } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  Platform,
  type LayoutChangeEvent,
} from 'react-native';
import {
  useLocalSearchParams,
  useRouter,
  useIsFocused,
  usePathname,
  type Href,
} from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { INCOME_TYPE_OPTIONS, isWageIncomeType } from '@perdiem/trpc/income-types';
import { trpc } from '../../../lib/trpc';
import {
  Badge,
  BrandSwitch,
  Button,
  Card,
  ErrorState,
  FormErrorBanner,
  LifeEventForm,
  LoadingSpinner,
  PageContainer,
  SectionHeader,
  Select,
  StateSelect,
  WebHead,
} from '../../../components';
import { useColors } from '../../../lib/colors';
import { formatFiAgeSummary } from '../../../lib/fire-summary';
import {
  formatRecurrence,
  formatLifeEventTaxLabel,
  formatYearReferenceLabel,
  LIFE_EVENT_TYPE_LABELS,
  normalizeLifeEventTaxTreatment,
  type EventYearReferencePerson,
  type GoalEvent,
  type GoalType,
  type LifeEventFormValues,
} from '../../../lib/life-events';
import {
  reportUiError,
  sendClientObservability,
  serializeClientError,
} from '../../../lib/observability';
import {
  buildScenarioNameFromSummary,
  summarizeScenarioOverrides,
} from '@perdiem/trpc/fire-forks-summary';
import { getScenarioIntentOption } from '../../../lib/scenario-intents';
import { formatCurrency } from '../../../lib/utils';
import { hasRunnableForkOverrides } from '@perdiem/trpc/fire-forks-overrides';
import type {
  ForkOverridesShape,
  ForkShape,
} from '@perdiem/trpc/fire-forks-types';

type AddedLifeEvent = NonNullable<ForkOverridesShape['addedLifeEvents']>[number];
type DraftIncomeStreamPause = {
  id: string;
  incomeStreamId: string;
  startYear: string;
  resumeYear: string;
};

type ScenarioIncomeStream = {
  id: string;
  name: string;
  type: string;
  annualAmount: number;
};

// ── Draft form model ─────────────────────────────────────────────────────────
//
// We store form state as percent-deltas (-25 means "−25%") and a state code
// override. At submit time we translate to the multiplier-based overrides JSON
// the API expects.

interface Draft {
  discretionaryPct: string; // signed integer string, "" = no override
  wagePct: string;
  incomePct: string;
  holdingsPct: string; // signed integer string, "" = no override
  retirementYearDelta: string; // signed integer string, "" = no override
  primaryState: string | null; // null = no override
  droppedIncomeStreamIds: string[];
  // v1 supports a single added income stream. ID is stable across edits so
  // the synthetic stream keeps the same identity even if monthly $ changes.
  addedIncomeId: string | null;
  addedIncomeName: string;
  addedIncomeMonthlyAmount: string;
  addedIncomeType: string;
  incomeStreamPauses: DraftIncomeStreamPause[];
  excludedLifeEventIds: string[];
  addedLifeEvents: AddedLifeEvent[];
  // v1 supports one price override on the household's largest holding.
  // We persist the holding ID alongside the price so we don't lose track if
  // "largest" changes later.
  largestHoldingId: string | null;
  largestHoldingOverridePrice: string;
}

const EMPTY_DRAFT: Draft = {
  discretionaryPct: '',
  wagePct: '',
  incomePct: '',
  holdingsPct: '',
  retirementYearDelta: '',
  primaryState: null,
  droppedIncomeStreamIds: [],
  addedIncomeId: null,
  addedIncomeName: '',
  addedIncomeMonthlyAmount: '',
  addedIncomeType: 'gig',
  incomeStreamPauses: [],
  excludedLifeEventIds: [],
  addedLifeEvents: [],
  largestHoldingId: null,
  largestHoldingOverridePrice: '',
};

const NO_SCENARIO_CHANGES_MESSAGE =
  'Make at least one scenario change before running a calculation.';

function normalizeAddedLifeEvent(event: AddedLifeEvent): AddedLifeEvent {
  const isRecurringExpense =
    event.type === 'expense' && event.repeatEveryYears != null;
  return {
    id: event.id,
    type: event.type,
    description: event.description,
    amount: event.amount ?? null,
    year: event.year,
    startYearRef: event.startYearRef,
    startYearPersonId: event.startYearPersonId ?? null,
    startYearAge: event.startYearAge ?? null,
    repeatEveryYears: isRecurringExpense ? event.repeatEveryYears! : null,
    endYear: isRecurringExpense ? (event.endYear ?? null) : null,
    endYearRef: isRecurringExpense ? (event.endYearRef ?? 'calendar_year') : null,
    endYearPersonId: isRecurringExpense
      ? (event.endYearPersonId ?? null)
      : null,
    endYearAge: isRecurringExpense ? (event.endYearAge ?? null) : null,
    growthRate: isRecurringExpense ? (event.growthRate ?? null) : null,
    isTaxable: event.type === 'expense' ? false : Boolean(event.isTaxable),
    taxTreatment:
      event.type === 'expense'
        ? null
        : normalizeLifeEventTaxTreatment(event.isTaxable, event.taxTreatment),
  };
}

function formValuesToAddedLifeEvent(values: LifeEventFormValues): AddedLifeEvent {
  return normalizeAddedLifeEvent({
    id: generateClientId(),
    type: values.type,
    description: values.description,
    amount: values.amount ?? null,
    year: values.year,
    startYearRef: values.startYearRef,
    startYearPersonId: values.startYearPersonId ?? null,
    startYearAge: values.startYearAge ?? null,
    repeatEveryYears: values.repeatEveryYears ?? null,
    endYear: values.endYear ?? null,
    endYearRef: values.endYearRef ?? null,
    endYearPersonId: values.endYearPersonId ?? null,
    endYearAge: values.endYearAge ?? null,
    growthRate: values.growthRate ?? null,
    isTaxable: values.isTaxable ?? false,
    taxTreatment: values.taxTreatment ?? null,
  });
}

function overridesToDraft(o: ForkOverridesShape | null | undefined): Draft {
  if (!o) return EMPTY_DRAFT;
  const s = o.scalars ?? {};
  const added = o.addedIncomeStreams?.[0] ?? null;
  const priceOverride = o.holdingPriceOverrides?.[0] ?? null;
  return {
    discretionaryPct:
      s.discretionaryMultiplier != null
        ? String(Math.round((s.discretionaryMultiplier - 1) * 100))
        : '',
    wagePct:
      s.wageMultiplier != null
        ? String(Math.round((s.wageMultiplier - 1) * 100))
        : '',
    incomePct:
      s.incomeMultiplier != null
        ? String(Math.round((s.incomeMultiplier - 1) * 100))
        : '',
    holdingsPct:
      s.holdingsMultiplier != null
        ? String(Math.round((s.holdingsMultiplier - 1) * 100))
        : '',
    retirementYearDelta:
      s.targetFireYearDelta != null ? String(s.targetFireYearDelta) : '',
    primaryState: o.persons?.primary?.state ?? null,
    droppedIncomeStreamIds: [...(o.droppedIncomeStreamIds ?? [])].sort(),
    excludedLifeEventIds: [...(o.excludedLifeEventIds ?? [])].sort(),
    addedIncomeId: added?.id ?? null,
    addedIncomeName: added?.name ?? '',
    addedIncomeMonthlyAmount:
      added?.monthlyAmount != null ? String(added.monthlyAmount) : '',
    addedIncomeType: added?.type ?? 'gig',
    incomeStreamPauses: (o.incomeStreamPauses ?? [])
      .map((pause) => ({
        id: pause.id,
        incomeStreamId: pause.incomeStreamId,
        startYear: String(pause.startYear),
        resumeYear: String(pause.resumeYear),
      }))
      .sort((a, b) => {
        const startDelta = Number(a.startYear) - Number(b.startYear);
        return startDelta !== 0
          ? startDelta
          : a.incomeStreamId.localeCompare(b.incomeStreamId);
      }),
    largestHoldingId: priceOverride?.holdingId ?? null,
    addedLifeEvents: (o.addedLifeEvents ?? []).map(normalizeAddedLifeEvent),
    largestHoldingOverridePrice:
      priceOverride?.overridePrice != null
        ? String(priceOverride.overridePrice)
        : '',
  };
}

function draftToOverrides(
  d: Draft,
  fallbackLargestHoldingId?: string | null,
): ForkOverridesShape {
  const scalars: ForkOverridesShape['scalars'] = {};
  const discretionary = parseSignedInt(d.discretionaryPct);
  if (discretionary !== null) {
    scalars.discretionaryMultiplier = 1 + discretionary / 100;
  }
  const wage = parseSignedInt(d.wagePct);
  if (wage !== null) scalars.wageMultiplier = 1 + wage / 100;
  const income = parseSignedInt(d.incomePct);
  if (income !== null) scalars.incomeMultiplier = 1 + income / 100;
  const holdings = parseSignedInt(d.holdingsPct);
  if (holdings !== null) scalars.holdingsMultiplier = 1 + holdings / 100;
  const yr = parseSignedInt(d.retirementYearDelta);
  if (yr !== null) scalars.targetFireYearDelta = yr;

  const overrides: ForkOverridesShape = {};
  if (Object.keys(scalars).length > 0) overrides.scalars = scalars;
  if (d.primaryState) overrides.persons = { primary: { state: d.primaryState } };
  if (d.droppedIncomeStreamIds.length > 0) {
    overrides.droppedIncomeStreamIds = [...d.droppedIncomeStreamIds];
  }
  if (d.excludedLifeEventIds.length > 0) {
    overrides.excludedLifeEventIds = [...d.excludedLifeEventIds].sort();
  }
  if (d.addedLifeEvents.length > 0) {
    overrides.addedLifeEvents = d.addedLifeEvents.map(normalizeAddedLifeEvent);
  }
  // Added income stream: include only when both name and a positive amount
  // are present. Stable ID is reused if the user is editing an existing row;
  // otherwise we mint one at save time (handled in runScenario below).
  const monthlyAmount = parseFloat(d.addedIncomeMonthlyAmount.trim());
  if (d.addedIncomeName.trim() && Number.isFinite(monthlyAmount) && monthlyAmount > 0) {
    overrides.addedIncomeStreams = [
      {
        id: d.addedIncomeId ?? generateClientId(),
        name: d.addedIncomeName.trim(),
        type: d.addedIncomeType || 'gig',
        monthlyAmount,
      },
    ];
  }
  const incomeStreamPauses = d.incomeStreamPauses
    .map((pause) => {
      const startYear = parseSignedInt(pause.startYear);
      const resumeYear = parseSignedInt(pause.resumeYear);
      if (
        !pause.incomeStreamId ||
        startYear === null ||
        resumeYear === null ||
        resumeYear <= startYear
      ) {
        return null;
      }
      return {
        id: pause.id,
        incomeStreamId: pause.incomeStreamId,
        startYear,
        resumeYear,
        incomeMultiplierDuringPause: 0,
      };
    })
    .filter((pause): pause is NonNullable<typeof pause> => pause !== null);
  if (incomeStreamPauses.length > 0) {
    overrides.incomeStreamPauses = incomeStreamPauses;
  }
  // Largest-holding price override: include when both holdingId is known
  // and a positive override price is entered.
  const overridePrice = parseFloat(d.largestHoldingOverridePrice.trim());
  const holdingId = d.largestHoldingId ?? fallbackLargestHoldingId ?? null;
  if (
    holdingId &&
    Number.isFinite(overridePrice) &&
    overridePrice > 0
  ) {
    overrides.holdingPriceOverrides = [
      { holdingId, overridePrice },
    ];
  }
  return overrides;
}

function generateClientId(): string {
  // Synthetic stream IDs need to be stable across re-renders within an edit
  // session. Real UUIDs aren't required (the backend doesn't FK-check), but
  // we use a UUID-like format to stay consistent with the rest of the system.
  const rand = () => Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
  return `${rand()}-${rand().slice(0, 4)}-${rand().slice(0, 4)}-${rand().slice(0, 4)}-${rand()}${rand().slice(0, 4)}`;
}

function parseSignedInt(s: string): number | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  if (!Number.isInteger(n)) return null;
  return n;
}

function reportForkUiError(
  error: unknown,
  params: {
    route: string | null;
    forkId: string | null;
    action: 'update' | 'calculate';
    message: string;
  },
) {
  const serialized = serializeClientError(error);
  const errorMessage = serialized.errorMessage ?? params.message;

  reportUiError({
    error,
    message: params.message,
    route: params.route,
    screen: 'fork-detail',
    component: 'ForkDetailScreen',
    metadata: {
      action: params.action,
      forkId: params.forkId,
    },
  });

  void sendClientObservability({
    kind: 'error',
    eventName: `fire_fork_${params.action}_failed`,
    route: params.route,
    screen: 'fork-detail',
    component: 'ForkDetailScreen',
    message: params.message,
    errorName: serialized.errorName ?? null,
    errorMessage,
    errorStack: serialized.errorStack ?? null,
    fingerprint: serialized.fingerprint ?? null,
    metadata: {
      action: params.action,
      forkId: params.forkId,
    },
  });
}

function draftEqualsFork(d: Draft, fork: ForkShape): boolean {
  const fromFork = overridesToDraft(fork.overrides);
  const sortedDraftDropped = [...d.droppedIncomeStreamIds].sort();
  const sortedDraftExcludedEvents = [...d.excludedLifeEventIds].sort();
  const sortedDraftPauses = normalizedDraftPauses(d.incomeStreamPauses);
  const sortedForkPauses = normalizedDraftPauses(fromFork.incomeStreamPauses);
  const forkHasPriceOverride = fromFork.largestHoldingOverridePrice.trim() !== '';
  const draftHasPriceOverride = d.largestHoldingOverridePrice.trim() !== '';
  const forkHoldingId = forkHasPriceOverride ? fromFork.largestHoldingId : null;
  const draftHoldingId = draftHasPriceOverride ? d.largestHoldingId : null;
  return (
    fromFork.discretionaryPct === d.discretionaryPct &&
    fromFork.wagePct === d.wagePct &&
    fromFork.incomePct === d.incomePct &&
    fromFork.holdingsPct === d.holdingsPct &&
    fromFork.retirementYearDelta === d.retirementYearDelta &&
    fromFork.primaryState === d.primaryState &&
    fromFork.droppedIncomeStreamIds.length === sortedDraftDropped.length &&
    fromFork.droppedIncomeStreamIds.every((id, i) => id === sortedDraftDropped[i]) &&
    fromFork.excludedLifeEventIds.length === sortedDraftExcludedEvents.length &&
    fromFork.excludedLifeEventIds.every((id, i) => id === sortedDraftExcludedEvents[i]) &&
    JSON.stringify(sortedForkPauses) === JSON.stringify(sortedDraftPauses) &&
    JSON.stringify(fromFork.addedLifeEvents) ===
      JSON.stringify(d.addedLifeEvents.map(normalizeAddedLifeEvent)) &&
    fromFork.addedIncomeName === d.addedIncomeName &&
    fromFork.addedIncomeMonthlyAmount === d.addedIncomeMonthlyAmount &&
    fromFork.addedIncomeType === d.addedIncomeType &&
    forkHoldingId === draftHoldingId &&
    fromFork.largestHoldingOverridePrice === d.largestHoldingOverridePrice
  );
}

function normalizedDraftPauses(pauses: DraftIncomeStreamPause[]) {
  return pauses
    .map((pause) => ({
      id: pause.id,
      incomeStreamId: pause.incomeStreamId,
      startYear: pause.startYear.trim(),
      resumeYear: pause.resumeYear.trim(),
    }))
    .sort((a, b) => {
      const startDelta = Number(a.startYear) - Number(b.startYear);
      if (startDelta !== 0) return startDelta;
      const streamDelta = a.incomeStreamId.localeCompare(b.incomeStreamId);
      return streamDelta !== 0 ? streamDelta : a.id.localeCompare(b.id);
    });
}

function validateDraft(d: Draft, streams: ScenarioIncomeStream[]): string | null {
  if (d.incomeStreamPauses.length === 0) return null;
  const streamIds = new Set(streams.map((stream) => stream.id));
  for (const pause of d.incomeStreamPauses) {
    const startYear = parseSignedInt(pause.startYear);
    const resumeYear = parseSignedInt(pause.resumeYear);
    if (!pause.incomeStreamId || !streamIds.has(pause.incomeStreamId)) {
      return 'Choose an income stream for each work break.';
    }
    if (startYear === null || resumeYear === null) {
      return 'Use whole calendar years for each work break.';
    }
    if (resumeYear <= startYear) {
      return 'Work resumes must be after the break starts.';
    }
  }
  return null;
}

type ScenarioEditorSectionId =
  | 'cashflow'
  | 'portfolio'
  | 'life-events'
  | 'retirement-tax';

function getInitialExpandedSection(d: Draft): ScenarioEditorSectionId {
  if (
    d.discretionaryPct.trim() ||
    d.wagePct.trim() ||
    d.incomePct.trim() ||
    d.droppedIncomeStreamIds.length > 0 ||
    d.incomeStreamPauses.length > 0 ||
    d.addedIncomeName.trim() ||
    d.addedIncomeMonthlyAmount.trim()
  ) {
    return 'cashflow';
  }
  if (d.holdingsPct.trim() || d.largestHoldingOverridePrice.trim()) {
    return 'portfolio';
  }
  if (d.excludedLifeEventIds.length > 0 || d.addedLifeEvents.length > 0) {
    return 'life-events';
  }
  if (d.retirementYearDelta.trim() || d.primaryState) {
    return 'retirement-tax';
  }
  return 'cashflow';
}

function summarizeChanges(count: number): string {
  if (count === 0) return 'No changes';
  return `${count} change${count === 1 ? '' : 's'}`;
}

function isForkCalculationRunning(fork: ForkShape): boolean {
  const status = fork.calculationStatus?.status;
  return (
    status === 'starting' ||
    status === 'precalculating' ||
    status === 'simulating' ||
    status === 'saving'
  );
}

function getForkCalculationError(fork: ForkShape): string | null {
  return fork.calculationStatus?.status === 'error'
    ? (fork.calculationStatus.error ?? 'Scenario calculation failed.')
    : null;
}

// ── Screen ───────────────────────────────────────────────────────────────────

export default function ForkDetailScreen() {
  const router = useRouter();
  const isFocused = useIsFocused();
  const pathname = usePathname();
  const { id, intent } = useLocalSearchParams<{
    id: string;
    intent?: string | string[];
  }>();
  const isNewScenario = id === 'new';
  const scenarioIntent = getScenarioIntentOption(intent);
  const utils = trpc.useUtils();
  const c = useColors();

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const initializedForkIdRef = useRef<string | null>(null);
  const scrollViewRef = useRef<ScrollView | null>(null);
  const lifeEventsSectionYRef = useRef<number | null>(null);
  const lifeEventFormYRef = useRef<number | null>(null);
  const scrolledGuidedIntentRef = useRef<string | null>(null);

  const forksQ = trpc.fire.forks.list.useQuery(undefined, {
    enabled: isFocused,
  });
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const planQ = trpc.fire.getPlan.useQuery(undefined, { enabled: isFocused });
  const incomeQ = trpc.income.list.useQuery(undefined, { enabled: isFocused });
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: isFocused });
  const eventsQ = trpc.fire.listEvents.useQuery(undefined, { enabled: isFocused });
  const largestHoldingQ = trpc.fire.forks.largestHolding.useQuery(undefined, {
    enabled: isFocused,
  });

  const fork = useMemo(
    () => (isNewScenario ? null : (forksQ.data?.find((f) => f.id === id) ?? null)),
    [forksQ.data, id, isNewScenario],
  );
  const isPreset = !isNewScenario && fork?.kind === 'preset';
  const isDemo = householdQ.data?.isDemo === true;
  const isReadOnly = Boolean(isPreset || isDemo || householdQ.data == null);
  const defaultBeginYear = planQ.data?.beginYear ?? null;
  const profiles = (profilesQ.data ?? []) as EventYearReferencePerson[];
  const lifeEvents = (eventsQ.data ?? []) as GoalEvent[];
  const incomeStreams = (incomeQ.data ?? []) as ScenarioIncomeStream[];
  const wageIncomeStreams = incomeStreams.filter((s) => isWageIncomeType(s.type));
  const primaryProfile = profiles.find((p) => p.type === 'primary');
  const spouseProfile = profiles.find((p) => p.type === 'spouse');
  const anyExplicitRetirementYear =
    (primaryProfile?.targetFireYear ?? null) != null ||
    (spouseProfile?.targetFireYear ?? null) != null;
  function describeForkFi(fiYear: number | null): string {
    const primaryFiAge =
      primaryProfile?.birthYear && fiYear
        ? fiYear - primaryProfile.birthYear
        : null;
    const spouseFiAge =
      spouseProfile?.birthYear && fiYear
        ? fiYear - spouseProfile.birthYear
        : null;
    return formatFiAgeSummary({
      fiYear,
      primaryName: primaryProfile?.firstName ?? null,
      primaryFiAge,
      spouseName: spouseProfile?.firstName ?? null,
      spouseFiAge,
    });
  }

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] =
    useState<ScenarioEditorSectionId | null>('cashflow');

  // Sync form when fork loads / changes
  useEffect(() => {
    if (isNewScenario) return;
    if (!fork) return;
    const nextDraft = overridesToDraft(fork.overrides);
    setDraft(nextDraft);
    if (initializedForkIdRef.current !== fork.id) {
      initializedForkIdRef.current = fork.id;
      setExpandedSection(getInitialExpandedSection(nextDraft));
    }
  }, [fork, isNewScenario]);

  useEffect(() => {
    if (!isNewScenario) return;
    initializedForkIdRef.current = 'new';
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
    setExpandedSection(
      scenarioIntent ? scenarioIntent.targetSection : 'cashflow',
    );
    setIsEditingName(false);
    setNameDraft('');
  }, [isNewScenario, scenarioIntent]);

  // Once the largest-holding lookup resolves, adopt the current holding's ID
  // as the target for new price overrides (only when the draft doesn't already
  // have one saved). Keeps the override aimed at the user's current #1.
  const largestHolding = largestHoldingQ.data?.largestHolding ?? null;
  useEffect(() => {
    if (!largestHolding) return;
    setDraft((d) =>
      d.largestHoldingId == null
        ? { ...d, largestHoldingId: largestHolding.id }
        : d,
    );
  }, [largestHolding]);

  const updateMutation = trpc.fire.forks.update.useMutation({
    onSuccess: (updated) => {
      utils.fire.forks.list.setData(undefined, (prev) =>
        prev?.map((f) => (f.id === updated.id ? updated : f)),
      );
      void utils.fire.forks.list.invalidate();
    },
    onError: (error) => {
      reportForkUiError(error, {
        route: pathname,
        forkId: typeof id === 'string' ? id : null,
        action: 'update',
        message: 'Failed to update scenario.',
      });
    },
  });
  const createMutation = trpc.fire.forks.create.useMutation({
    onSuccess: (created) => {
      utils.fire.forks.list.setData(undefined, (prev) =>
        prev ? [...prev, created] : [created],
      );
      void utils.fire.forks.list.invalidate();
    },
    onError: (error) => {
      reportForkUiError(error, {
        route: pathname,
        forkId: null,
        action: 'update',
        message: error.message || 'Failed to create scenario.',
      });
    },
  });
  const deleteMutation = trpc.fire.forks.delete.useMutation({
    onSuccess: () => {
      utils.fire.forks.list.invalidate();
      router.replace('/(app)/scenarios' as Href);
    },
  });
  const calculateMutation = trpc.fire.forks.calculate.useMutation({
    onSettled: () => {
      void utils.fire.forks.list.invalidate();
    },
    onError: (error) => {
      const message = error.message || 'Scenario calculation failed.';
      setDraftError(message);
      reportForkUiError(error, {
        route: pathname,
        forkId: typeof id === 'string' ? id : null,
        action: 'calculate',
        message,
      });
    },
  });

  if (!isNewScenario && forksQ.isLoading) {
    return (
      <PageContainer className="p-4" size="default">
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!isNewScenario && forksQ.error) {
    return (
      <ErrorState
        message={`Couldn't load scenario: ${forksQ.error.message}`}
        onRetry={() => forksQ.refetch()}
      />
    );
  }

  if (!isNewScenario && !fork) {
    return (
      <ErrorState
        message="Scenario not found. It may have been deleted."
        onRetry={() => router.replace('/(app)/scenarios' as Href)}
        retryLabel="Back to scenarios"
      />
    );
  }

  const currentFork = isNewScenario ? null : fork!;
  const activeOtherFork = forksQ.data?.find(
    (candidate) =>
      candidate.id !== currentFork?.id && isForkCalculationRunning(candidate),
  ) ?? null;
  const candidateOverrides = draftToOverrides(draft, largestHolding?.id ?? null);
  const scenarioSummaryBullets = summarizeScenarioOverrides(candidateOverrides, {
    wageStreamNames: new Map(incomeStreams.map((stream) => [stream.id, stream.name])),
    holdingLabels: new Map(
      largestHolding
        ? [
            [
              largestHolding.id,
              largestHolding.symbol ?? largestHolding.name ?? 'Largest holding',
            ],
          ]
        : [],
    ),
    lifeEventNames: new Map(
      lifeEvents.map((event) => [event.id, event.description]),
    ),
  });
  const generatedScenarioName = buildScenarioNameFromSummary(
    scenarioSummaryBullets,
  );
  const hasRunnableScenarioChanges = hasRunnableForkOverrides(candidateOverrides);
  const isDirty = isNewScenario
    ? hasRunnableScenarioChanges
    : !draftEqualsFork(draft, currentFork!);
  const isCalculatingFork = currentFork ? isForkCalculationRunning(currentFork) : false;
  const calculationError = currentFork ? getForkCalculationError(currentFork) : null;
  const hasCalculationWork = isNewScenario
    ? hasRunnableScenarioChanges
    : hasRunnableScenarioChanges &&
      (isDirty ||
        currentFork!.isStale ||
        currentFork!.lastResult == null ||
        calculationError != null);
  const isBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    calculateMutation.isPending ||
    isCalculatingFork ||
    deleteMutation.isPending;
  const runScenarioDisabled =
    isBusy ||
    isReadOnly ||
    (isPreset && isDirty) ||
    activeOtherFork != null ||
    !hasCalculationWork;
  const runScenarioBlockMessage =
    activeOtherFork != null
      ? 'Another scenario is recalculating. Wait for it to finish before running this one.'
      : !hasCalculationWork
        ? hasRunnableScenarioChanges
          ? 'Change a scenario input before running the calculation again.'
          : NO_SCENARIO_CHANGES_MESSAGE
        : null;
  const pageTitle = isNewScenario
    ? (nameDraft.trim() || 'New scenario')
    : currentFork!.name;
  const guidedLifeEventIntentId =
    isNewScenario && scenarioIntent?.lifeEventType ? scenarioIntent.id : null;
  const cashflowChangeCount = [
    draft.discretionaryPct.trim(),
    draft.wagePct.trim(),
    draft.incomePct.trim(),
    draft.droppedIncomeStreamIds.length > 0 ? 'jobs' : '',
    draft.incomeStreamPauses.length > 0 ? 'work-breaks' : '',
    draft.addedIncomeName.trim() || draft.addedIncomeMonthlyAmount.trim()
      ? 'added-income'
      : '',
  ].filter(Boolean).length;
  const portfolioChangeCount = [
    draft.holdingsPct.trim(),
    draft.largestHoldingOverridePrice.trim(),
  ].filter(Boolean).length;
  const excludedLifeEventSet = new Set(draft.excludedLifeEventIds);
  const excludedVisibleLifeEvents = lifeEvents.filter((event) =>
    excludedLifeEventSet.has(event.id),
  ).length;
  const simulatedLifeEventCount = draft.addedLifeEvents.length;
  const lifeEventsSummary = eventsQ.isLoading
    ? 'Loading events'
    : [
        lifeEvents.length === 0
          ? 'No saved events'
          : `${Math.max(0, lifeEvents.length - excludedVisibleLifeEvents)}/${lifeEvents.length} included`,
        simulatedLifeEventCount > 0
          ? `${simulatedLifeEventCount} simulated`
          : '',
      ]
        .filter(Boolean)
        .join(', ');
  const retirementTaxChangeCount = [
    draft.retirementYearDelta.trim(),
    draft.primaryState,
  ].filter(Boolean).length;

  function toggleSection(section: ScenarioEditorSectionId) {
    setExpandedSection((current) => (current === section ? null : section));
  }

  function toggleLifeEvent(eventId: string, include: boolean) {
    setDraft((d) => {
      const next = new Set(d.excludedLifeEventIds);
      if (include) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return { ...d, excludedLifeEventIds: Array.from(next).sort() };
    });
  }

  function addSimulatedLifeEvent(values: LifeEventFormValues) {
    const event = formValuesToAddedLifeEvent(values);
    setDraft((d) => ({
      ...d,
      addedLifeEvents: [...d.addedLifeEvents, event],
    }));
  }

  function removeSimulatedLifeEvent(eventId: string) {
    setDraft((d) => ({
      ...d,
      addedLifeEvents: d.addedLifeEvents.filter((event) => event.id !== eventId),
    }));
  }

  function addWorkBreak() {
    const usedStreamIds = new Set(
      draft.incomeStreamPauses.map((pause) => pause.incomeStreamId),
    );
    const stream =
      wageIncomeStreams.find((candidate) => !usedStreamIds.has(candidate.id)) ??
      wageIncomeStreams[0];
    if (!stream) return;
    const startYear = new Date().getFullYear() + 1;
    setDraft((d) => ({
      ...d,
      incomeStreamPauses: [
        ...d.incomeStreamPauses,
        {
          id: generateClientId(),
          incomeStreamId: stream.id,
          startYear: String(startYear),
          resumeYear: String(startYear + 1),
        },
      ],
    }));
  }

  function updateWorkBreak(id: string, patch: Partial<DraftIncomeStreamPause>) {
    setDraft((d) => ({
      ...d,
      incomeStreamPauses: d.incomeStreamPauses.map((pause) =>
        pause.id === id ? { ...pause, ...patch } : pause,
      ),
    }));
  }

  function removeWorkBreak(id: string) {
    setDraft((d) => ({
      ...d,
      incomeStreamPauses: d.incomeStreamPauses.filter((pause) => pause.id !== id),
    }));
  }

  function scrollToGuidedLifeEventForm() {
    if (!guidedLifeEventIntentId) return;
    if (scrolledGuidedIntentRef.current === guidedLifeEventIntentId) return;
    const sectionY = lifeEventsSectionYRef.current;
    const formY = lifeEventFormYRef.current;
    if (sectionY == null || formY == null) return;

    scrolledGuidedIntentRef.current = guidedLifeEventIntentId;
    setTimeout(() => {
      scrollViewRef.current?.scrollTo({
        y: Math.max(0, sectionY + formY - 16),
        animated: true,
      });
    }, 80);
  }

  function handleLifeEventsSectionLayout(event: LayoutChangeEvent) {
    lifeEventsSectionYRef.current = event.nativeEvent.layout.y;
    scrollToGuidedLifeEventForm();
  }

  function handleLifeEventFormLayout(event: LayoutChangeEvent) {
    lifeEventFormYRef.current = event.nativeEvent.layout.y;
    scrollToGuidedLifeEventForm();
  }

  function getRunScenarioLabel() {
    return isNewScenario ? 'Create and run scenario' : 'Save and run scenario';
  }

  async function saveAndRunScenario() {
    setDraftError(null);
    if (isDemo) {
      setDraftError('Demo scenarios are read-only.');
      return;
    }
    if (activeOtherFork) {
      setDraftError('Another scenario is recalculating. Wait for it to finish before running this one.');
      return;
    }
    if (!hasRunnableScenarioChanges) {
      setDraftError(NO_SCENARIO_CHANGES_MESSAGE);
      return;
    }
    if (!hasCalculationWork) {
      setDraftError('Change a scenario input before running the calculation again.');
      return;
    }
    if (isPreset && isDirty) {
      setDraftError('Preset scenarios are read-only.');
      return;
    }
    const validationError = validateDraft(draft, incomeStreams);
    if (validationError) {
      setDraftError(validationError);
      return;
    }
    try {
      if (isNewScenario) {
        const created = await createMutation.mutateAsync({
          name: nameDraft.trim() || generatedScenarioName,
          overrides: candidateOverrides,
        });
        utils.fire.forks.list.setData(undefined, (prev) =>
          (prev ?? [created]).map((item) =>
            item.id === created.id
              ? {
                  ...item,
                  isStale: true,
                  calculationStatus: { progress: 0, status: 'starting' },
                }
              : item,
          ),
        );
        router.replace('/(app)/scenarios' as Href);
        calculateMutation.mutate({ id: created.id });
        return;
      }
      const forkId = currentFork!.id;
      const trimmedName = nameDraft.trim();
      const nameChanged =
        isEditingName && trimmedName.length > 0 && trimmedName !== currentFork!.name;
      if (isEditingName && trimmedName.length === 0) {
        setDraftError('Give your scenario a short name before saving.');
        return;
      }
      if (isDirty || nameChanged) {
        await updateMutation.mutateAsync({
          id: forkId,
          ...(nameChanged ? { name: trimmedName } : {}),
          ...(isDirty ? { overrides: candidateOverrides } : {}),
        });
        setIsEditingName(false);
      }
      utils.fire.forks.list.setData(undefined, (prev) =>
        prev?.map((item) =>
          item.id === forkId
            ? {
                ...item,
                isStale: true,
                calculationStatus: { progress: 0, status: 'starting' },
              }
            : item,
        ),
      );
      router.replace('/(app)/scenarios' as Href);
      calculateMutation.mutate({ id: forkId });
    } catch {
      // mutation errors surface in FormErrorBanner via the mutation state
    }
  }

  function onDelete() {
    if (isReadOnly || !currentFork) return;
    const confirmAndDelete = () => deleteMutation.mutate({ id: currentFork.id });
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.confirm(`Delete "${currentFork.name}"?`)) {
        confirmAndDelete();
      }
      return;
    }
    Alert.alert(
      'Delete scenario?',
      `"${currentFork.name}" will be removed.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: confirmAndDelete },
      ],
    );
  }

  return (
    <>
      <WebHead><title>{pageTitle} — Per Diem</title></WebHead>
      <ScrollView
        ref={scrollViewRef}
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 24 }}
      >
        <PageContainer className="p-4" size="default">
          <View className="flex-row items-start justify-between mb-4">
            <View className="flex-1 pr-3">
              {isNewScenario ? (
                <View>
                  <Text className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
                    Scenario name
                  </Text>
                  <TextInput
                    value={nameDraft}
                    onChangeText={setNameDraft}
                    placeholder={scenarioIntent?.namePlaceholder ?? 'e.g. Move to Texas'}
                    maxLength={120}
                    className="text-xl font-bold text-gray-800 dark:text-gray-200 border-b border-gray-300 dark:border-gray-700 pb-2"
                    placeholderTextColor="#9ca3af"
                  />
                </View>
              ) : isEditingName ? (
                <View className="flex-row items-center gap-2">
                  <TextInput
                    value={nameDraft}
                    onChangeText={setNameDraft}
                    autoFocus
                    maxLength={120}
                    className="flex-1 text-xl font-bold text-gray-800 dark:text-gray-200 border-b border-forest-600 dark:border-forest-500 pb-1"
                    onSubmitEditing={() => {
                      if (isReadOnly) {
                        setIsEditingName(false);
                        return;
                      }
                      const trimmed = nameDraft.trim();
                      if (!trimmed || trimmed === currentFork!.name) {
                        setIsEditingName(false);
                        return;
                      }
                      updateMutation.mutate(
                        { id: currentFork!.id, name: trimmed },
                        { onSuccess: () => setIsEditingName(false) },
                      );
                    }}
                  />
                  <Button
                    size="sm"
                    onPress={() => {
                      if (isReadOnly) {
                        setIsEditingName(false);
                        return;
                      }
                      const trimmed = nameDraft.trim();
                      if (!trimmed || trimmed === currentFork!.name) {
                        setIsEditingName(false);
                        return;
                      }
                      updateMutation.mutate(
                        { id: currentFork!.id, name: trimmed },
                        { onSuccess: () => setIsEditingName(false) },
                      );
                    }}
                    loading={updateMutation.isPending}
                    disabled={updateMutation.isPending}
                  >
                    <Text className="text-white font-semibold">Save</Text>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onPress={() => setIsEditingName(false)}
                    disabled={updateMutation.isPending}
                  >
                    <Text className="text-forest-700 dark:text-green-500 font-semibold">
                      Cancel
                    </Text>
                  </Button>
                </View>
              ) : (
                <View className="flex-row items-center gap-2 flex-wrap">
                  <Text className="text-xl font-bold text-gray-800 dark:text-gray-200">
                    {currentFork!.name}
                  </Text>
                  {!isReadOnly && (
                    <Pressable
                      onPress={() => {
                        if (isReadOnly) return;
                        setNameDraft(currentFork!.name);
                        setIsEditingName(true);
                      }}
                      hitSlop={8}
                      accessibilityRole="button"
                      accessibilityLabel="Rename scenario"
                    >
                      <Ionicons name="pencil" size={16} color={c.textSecondary} />
                    </Pressable>
                  )}
                  {isPreset && <Badge variant="gray" label="Preset" />}
                </View>
              )}
            </View>
            <Pressable
              onPress={() => router.replace('/(app)/scenarios' as Href)}
              hitSlop={8}
              className="p-1"
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Ionicons name="close" size={24} color={c.textSecondary} />
            </Pressable>
          </View>

          {draftError && <FormErrorBanner error={draftError} />}
          {isDemo && (
            <Card className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Text className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                Read-only demo account
              </Text>
              <Text className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                Scenario editing, recalculation, and deletion are disabled in demos.
              </Text>
            </Card>
          )}
          <FormErrorBanner
            error={
              createMutation.error?.message ??
              updateMutation.error?.message ??
              calculateMutation.error?.message ??
              deleteMutation.error?.message ??
              null
            }
          />

          <SectionHeader title="Scenario Inputs" />
          <ScenarioAccordionSection
            title="Income & Spending"
            summary={summarizeChanges(cashflowChangeCount)}
            expanded={expandedSection === 'cashflow'}
            onToggle={() => toggleSection('cashflow')}
          >
            <PercentField
              label="Discretionary spending"
              hint="Adjusts entertainment, food, travel, and similar categories."
              value={draft.discretionaryPct}
              onChange={(v) => setDraft((d) => ({ ...d, discretionaryPct: v }))}
              readOnly={isReadOnly}
            />
            <PercentField
              label="Wage income"
              hint="Salary and self-employment income (e.g. higher- or lower-paying job)."
              value={draft.wagePct}
              onChange={(v) => setDraft((d) => ({ ...d, wagePct: v }))}
              readOnly={isReadOnly}
            />
            <WageStreamToggles
              streams={wageIncomeStreams}
              droppedIds={draft.droppedIncomeStreamIds}
              onToggle={(id) =>
                setDraft((d) => ({
                  ...d,
                  droppedIncomeStreamIds: d.droppedIncomeStreamIds.includes(id)
                    ? d.droppedIncomeStreamIds.filter((x) => x !== id)
                    : [...d.droppedIncomeStreamIds, id],
                  incomeStreamPauses: d.droppedIncomeStreamIds.includes(id)
                    ? d.incomeStreamPauses
                    : d.incomeStreamPauses.filter(
                        (pause) => pause.incomeStreamId !== id,
                      ),
                }))
              }
              readOnly={isReadOnly}
            />
            <WorkBreakRows
              streams={wageIncomeStreams}
              pauses={draft.incomeStreamPauses}
              droppedIds={draft.droppedIncomeStreamIds}
              onAdd={addWorkBreak}
              onUpdate={updateWorkBreak}
              onRemove={removeWorkBreak}
              readOnly={isReadOnly}
            />
            <PercentField
              label="All income"
              hint="Scales wages and passive income together."
              value={draft.incomePct}
              onChange={(v) => setDraft((d) => ({ ...d, incomePct: v }))}
              readOnly={isReadOnly}
            />
            <AddedIncomeRow
              draft={draft}
              onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
              readOnly={isReadOnly}
            />
          </ScenarioAccordionSection>

          <ScenarioAccordionSection
            title="Portfolio"
            summary={summarizeChanges(portfolioChangeCount)}
            expanded={expandedSection === 'portfolio'}
            onToggle={() => toggleSection('portfolio')}
          >
            <PercentField
              label="Market holdings"
              hint="Scales all market-exposed accounts (stocks, bonds, ETFs). Cash is left alone."
              value={draft.holdingsPct}
              onChange={(v) => setDraft((d) => ({ ...d, holdingsPct: v }))}
              readOnly={isReadOnly}
            />
            {largestHolding && (
              <LargestHoldingPriceField
                holding={largestHolding}
                value={draft.largestHoldingOverridePrice}
                onChange={(v) =>
                  setDraft((d) => ({
                    ...d,
                    largestHoldingId: d.largestHoldingId ?? largestHolding.id,
                    largestHoldingOverridePrice: v,
                  }))
                }
                readOnly={isReadOnly}
              />
            )}
          </ScenarioAccordionSection>

          <ScenarioAccordionSection
            title="Life Events"
            summary={lifeEventsSummary}
            expanded={expandedSection === 'life-events'}
            onToggle={() => toggleSection('life-events')}
            onLayout={handleLifeEventsSectionLayout}
          >
            <LifeEventToggles
              events={lifeEvents}
              addedEvents={draft.addedLifeEvents}
              profiles={profiles}
              currentFireYear={planQ.data?.beginYear ?? null}
              defaultPlanEndYear={planQ.data?.endYear ?? new Date().getFullYear() + 30}
              excludedIds={draft.excludedLifeEventIds}
              onToggle={toggleLifeEvent}
              onAddSimulated={addSimulatedLifeEvent}
              onRemoveSimulated={removeSimulatedLifeEvent}
              isLoading={eventsQ.isLoading}
              readOnly={isReadOnly}
              {...(scenarioIntent?.lifeEventType
                ? {
                    initialAddType: scenarioIntent.lifeEventType,
                    startAdding: isNewScenario,
                    intentHint: scenarioIntent.lifeEventHint,
                    onAddFormLayout: handleLifeEventFormLayout,
                  }
                : {})}
            />
          </ScenarioAccordionSection>

          <ScenarioAccordionSection
            title="Retirement & Tax"
            summary={summarizeChanges(retirementTaxChangeCount)}
            expanded={expandedSection === 'retirement-tax'}
            onToggle={() => toggleSection('retirement-tax')}
          >
            <View className="mb-3">
              <Text
                className={
                  'text-sm font-medium ' +
                  (anyExplicitRetirementYear
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-400 dark:text-gray-500')
                }
              >
                Retire ± years earlier/later
              </Text>
              <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {anyExplicitRetirementYear
                  ? 'Shifts every explicit retirement year. Leave blank for no change.'
                  : 'Set a target retirement year in your profile to enable this.'}
              </Text>
              <TextInput
                value={draft.retirementYearDelta}
                onChangeText={(v) =>
                  setDraft((d) => ({ ...d, retirementYearDelta: v }))
                }
                editable={!isReadOnly && anyExplicitRetirementYear}
                keyboardType="numbers-and-punctuation"
                placeholder="e.g. -2"
                placeholderTextColor="#9ca3af"
                className={
                  'border rounded-xl px-4 py-3 ' +
                  (anyExplicitRetirementYear
                    ? 'border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200'
                    : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500')
                }
              />
            </View>
            <View>
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Move to another state
              </Text>
              <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Override the state of residence for tax purposes.
              </Text>
              {isReadOnly ? (
                <Text className="text-sm text-gray-500 italic">
                  {draft.primaryState ?? 'No change'}
                </Text>
              ) : (
                <>
                  <StateSelect
                    value={draft.primaryState ?? ''}
                    onChange={(v) => setDraft((d) => ({ ...d, primaryState: v }))}
                  />
                  {draft.primaryState && (
                    <Pressable
                      onPress={() => setDraft((d) => ({ ...d, primaryState: null }))}
                      className="mt-2 self-start"
                    >
                      <Text className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                        Clear state override
                      </Text>
                    </Pressable>
                  )}
                </>
              )}
            </View>
          </ScenarioAccordionSection>

          {!isDemo && (
            <View>
              <Button
                onPress={saveAndRunScenario}
                loading={
                  createMutation.isPending ||
                  updateMutation.isPending ||
                  calculateMutation.isPending ||
                  isCalculatingFork
                }
                disabled={runScenarioDisabled}
              >
                <Text className="text-white font-semibold">
                  {getRunScenarioLabel()}
                </Text>
              </Button>
              {runScenarioBlockMessage && (
                <Text className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {runScenarioBlockMessage}
                </Text>
              )}
            </View>
          )}

          {!isNewScenario && !isReadOnly && (
            <View className="mt-3 items-center">
              <Pressable onPress={onDelete} disabled={isBusy} hitSlop={8}>
                <Text className="text-sm font-semibold text-red-600 dark:text-red-400">
                  Delete scenario
                </Text>
              </Pressable>
            </View>
          )}

          <SectionHeader title="Results" className="mt-6" />
          <Card>
            {isNewScenario ? (
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                Add a scenario change, then create and run the scenario.
              </Text>
            ) : isCalculatingFork || updateMutation.isPending || calculateMutation.isPending ? (
              <View className="flex-row items-center gap-2">
                <ActivityIndicator size="small" />
                <Text className="text-sm text-gray-600 dark:text-gray-400">
                  Recalculating…
                </Text>
              </View>
            ) : calculationError ? (
              <Text className="text-sm text-red-700 dark:text-red-400">
                {calculationError}
              </Text>
            ) : currentFork!.isStale ? (
              <Text className="text-sm text-amber-700 dark:text-amber-500">
                {isDemo
                  ? 'Default plan changed. Scenario recalculation is disabled in demos.'
                  : 'Default plan changed. Recalculate this scenario to compare it against the latest plan.'}
              </Text>
            ) : currentFork!.lastResult == null ? (
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                {isDemo
                  ? 'Not calculated yet. Scenario recalculation is disabled in demos.'
                  : 'Not calculated yet. Adjust the inputs above, then save and run the scenario.'}
              </Text>
            ) : currentFork!.lastResult.beginYear == null ? (
              <View>
                <Text className="text-base font-semibold text-red-700 dark:text-red-400 mb-2">
                  No path to FI under these assumptions
                </Text>
                <Text className="text-sm text-gray-700 dark:text-gray-300">
                  Best simulated success rate:{' '}
                  {currentFork!.lastResult.successRate != null
                    ? `${(currentFork!.lastResult.successRate * 100).toFixed(0)}%`
                    : '—'}{' '}
                  (below the {(currentFork!.lastResult.threshold * 100).toFixed(0)}% threshold).
                </Text>
                <Text className="text-xs text-gray-400 mt-3">
                  Last computed{' '}
                  {new Date(currentFork!.lastResult.computedAt).toLocaleString()}
                </Text>
              </View>
            ) : (
              <View>
                <Text className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                  {describeForkFi(currentFork!.lastResult.beginYear)}
                </Text>
                <Text className="text-sm text-gray-700 dark:text-gray-300">
                  Success rate:{' '}
                  {currentFork!.lastResult.successRate != null
                    ? `${(currentFork!.lastResult.successRate * 100).toFixed(0)}%`
                    : '—'}
                </Text>
                {defaultBeginYear != null && (
                  <View className="flex-row items-center gap-2 mt-2 flex-wrap">
                    <Text className="text-xs text-gray-500">
                      Default plan: {describeForkFi(defaultBeginYear)}
                    </Text>
                    {(() => {
                      const delta = currentFork!.lastResult.beginYear - defaultBeginYear;
                      if (delta === 0) {
                        return <Badge variant="gray" label="Same as default" />;
                      }
                      const n = Math.abs(delta);
                      const noun = n === 1 ? 'year' : 'years';
                      const tone = delta < 0 ? 'earlier' : 'later';
                      return (
                        <Badge
                          variant={delta < 0 ? 'green' : 'red'}
                          label={`${n} ${noun} ${tone}`}
                        />
                      );
                    })()}
                  </View>
                )}
                <Text className="text-xs text-gray-400 mt-3">
                  Last computed{' '}
                  {new Date(currentFork!.lastResult.computedAt).toLocaleString()}
                </Text>
              </View>
            )}
          </Card>
        </PageContainer>
      </ScrollView>
    </>
  );
}

interface LargestHoldingPriceFieldProps {
  holding: {
    id: string;
    symbol: string | null;
    name: string | null;
    quantity: number;
    currentPrice: number;
  };
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}

interface ScenarioAccordionSectionProps {
  title: string;
  summary: string;
  expanded: boolean;
  onToggle: () => void;
  onLayout?: (event: LayoutChangeEvent) => void;
  children: ReactNode;
}

function ScenarioAccordionSection({
  title,
  summary,
  expanded,
  onToggle,
  onLayout,
  children,
}: ScenarioAccordionSectionProps) {
  const c = useColors();
  return (
    <Card className="mb-3" onLayout={onLayout}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
        onPress={onToggle}
        className="active:opacity-60"
      >
        <SectionHeader
          title={title}
          action={
            <View className="flex-row items-center gap-2">
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                {summary}
              </Text>
              <Ionicons
                name={expanded ? 'chevron-up' : 'chevron-down'}
                size={16}
                color={c.textSecondary}
              />
            </View>
          }
        />
      </Pressable>
      {expanded && <View className="pt-1">{children}</View>}
    </Card>
  );
}

interface LifeEventTogglesProps {
  events: GoalEvent[];
  addedEvents: AddedLifeEvent[];
  profiles: EventYearReferencePerson[];
  currentFireYear: number | null | undefined;
  defaultPlanEndYear: number;
  excludedIds: string[];
  onToggle: (eventId: string, include: boolean) => void;
  onAddSimulated: (values: LifeEventFormValues) => void;
  onRemoveSimulated: (eventId: string) => void;
  isLoading?: boolean;
  readOnly?: boolean;
  initialAddType?: GoalType;
  startAdding?: boolean;
  intentHint?: string;
  onAddFormLayout?: (event: LayoutChangeEvent) => void;
}

function LifeEventToggles({
  events,
  addedEvents,
  profiles,
  currentFireYear,
  defaultPlanEndYear,
  excludedIds,
  onToggle,
  onAddSimulated,
  onRemoveSimulated,
  isLoading,
  readOnly,
  initialAddType,
  startAdding = false,
  intentHint,
  onAddFormLayout,
}: LifeEventTogglesProps) {
  const [adding, setAdding] = useState(startAdding);
  const excluded = new Set(excludedIds);

  useEffect(() => {
    if (startAdding) setAdding(true);
  }, [startAdding]);

  if (isLoading) {
    return (
      <Text className="text-sm text-gray-500 dark:text-gray-400">
        Loading life events…
      </Text>
    );
  }

  return (
    <View>
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {intentHint ??
          'Turn off saved events or add simulated ones for this scenario. Your main FIRE plan is unchanged.'}
      </Text>
      {events.length === 0 && addedEvents.length === 0 ? (
        <Text className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          No life events are configured on the current plan.
        </Text>
      ) : null}
      {(events.length > 0 || addedEvents.length > 0) && (
        <View className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {events.map((event, index) => {
            const included = !excluded.has(event.id);
            const amount = Number(event.amount ?? 0);
            const amountLabel = `${event.type === 'expense' ? '-' : '+'}${formatCurrency(Math.abs(amount), { decimals: 0, compact: true })}`;
            const timingLabel = formatYearReferenceLabel(
              {
                ref: event.startYearRef ?? 'calendar_year',
                year: event.year,
                personId: event.startYearPersonId,
                age: event.startYearAge,
              },
              profiles,
              currentFireYear,
            );
            const recurrenceLabel =
              event.type === 'expense'
                ? formatRecurrence(
                    event.repeatEveryYears,
                    event.growthRate,
                    event.endYear,
                  )
                : null;
            const taxLabel = formatLifeEventTaxLabel(event);

            return (
              <View
                key={event.id}
                className={
                  'flex-row items-start gap-3 px-4 py-3 ' +
                  (index > 0 ? 'border-t border-gray-200 dark:border-gray-700 ' : '') +
                  (!included ? 'bg-gray-50 dark:bg-gray-800/60' : '')
                }
              >
                <View className="flex-1 pr-2">
                  <View className="flex-row items-center gap-2 flex-wrap">
                    <Text
                      className={
                        'text-sm font-medium ' +
                        (included
                          ? 'text-gray-800 dark:text-gray-200'
                          : 'text-gray-500 dark:text-gray-400 line-through')
                      }
                    >
                      {event.description}
                    </Text>
                    <Badge
                      variant={event.type === 'expense' ? 'gray' : 'green'}
                      label={LIFE_EVENT_TYPE_LABELS[event.type]}
                    />
                  </View>
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {amountLabel} • {timingLabel}
                  </Text>
                  {recurrenceLabel ? (
                    <Text className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {recurrenceLabel}
                    </Text>
                  ) : null}
                  {taxLabel ? (
                    <Text className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {taxLabel}
                    </Text>
                  ) : null}
                </View>
                <BrandSwitch
                  value={included}
                  onValueChange={(next) => onToggle(event.id, next)}
                  disabled={readOnly}
                  accessibilityLabel={`Include ${event.description}`}
                />
              </View>
            );
          })}
          {addedEvents.map((event, index) => {
            const amount = Number(event.amount ?? 0);
            const amountLabel = `${event.type === 'expense' ? '-' : '+'}${formatCurrency(Math.abs(amount), { decimals: 0, compact: true })}`;
            const timingLabel = formatYearReferenceLabel(
              {
                ref: event.startYearRef,
                year: event.year,
                personId: event.startYearPersonId ?? null,
                age: event.startYearAge ?? null,
              },
              profiles,
              currentFireYear,
            );
            const recurrenceLabel =
              event.type === 'expense'
                ? formatRecurrence(
                    event.repeatEveryYears,
                    event.growthRate,
                    event.endYear,
                  )
                : null;
            const taxLabel = formatLifeEventTaxLabel(event);
            const rowIndex = events.length + index;

            return (
              <View
                key={event.id}
                className={
                  'flex-row items-start gap-3 px-4 py-3 bg-green-50 dark:bg-green-900/20 ' +
                  (rowIndex > 0 ? 'border-t border-gray-200 dark:border-gray-700' : '')
                }
              >
                <View className="flex-1 pr-2">
                  <View className="flex-row items-center gap-2 flex-wrap">
                    <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {event.description}
                    </Text>
                    <Badge variant="green" label="Simulated" />
                    <Badge variant={event.type === 'expense' ? 'gray' : 'green'} label={LIFE_EVENT_TYPE_LABELS[event.type]} />
                  </View>
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {amountLabel} • {timingLabel}
                  </Text>
                  {recurrenceLabel ? (
                    <Text className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {recurrenceLabel}
                    </Text>
                  ) : null}
                  {taxLabel ? (
                    <Text className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {taxLabel}
                    </Text>
                  ) : null}
                </View>
                {!readOnly && (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Remove simulated event ${event.description}`}
                    hitSlop={8}
                    onPress={() => onRemoveSimulated(event.id)}
                    className="p-1"
                  >
                    <Ionicons name="trash-outline" size={18} color="#dc2626" />
                  </Pressable>
                )}
              </View>
            );
          })}
        </View>
      )}

      {!readOnly && (
        adding ? (
          <View
            className="mt-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3"
            onLayout={onAddFormLayout}
          >
            <LifeEventForm
              profiles={profiles}
              currentFireYear={currentFireYear}
              defaultPlanEndYear={defaultPlanEndYear}
              {...(initialAddType ? { initialType: initialAddType } : {})}
              onSave={async (values) => {
                onAddSimulated(values);
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
              saving={false}
              saveLabel="Add simulated event"
            />
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Add a simulated life event"
            onPress={() => setAdding(true)}
            className="mt-3 rounded-xl border border-gray-300 dark:border-gray-600 px-4 py-4 flex-row items-center justify-center gap-2 active:opacity-70"
            style={{ borderStyle: 'dotted' }}
          >
            <Ionicons name="add-circle-outline" size={18} color="#2B7A50" />
            <Text className="text-sm font-semibold text-forest-700 dark:text-green-500">
              Add a simulated life event
            </Text>
          </Pressable>
        )
      )}
    </View>
  );
}

function LargestHoldingPriceField({
  holding,
  value,
  onChange,
  readOnly,
}: LargestHoldingPriceFieldProps) {
  const displayName = holding.symbol ?? holding.name ?? 'Largest holding';
  const formatPrice = (p: number) =>
    `$${p.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <View className="mb-3">
      <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {displayName} price
      </Text>
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        {`Simulate a different price for your largest holding (${holding.quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })} shares; currently ${formatPrice(holding.currentPrice)}).`}
      </Text>
      <View className="flex-row items-center">
        <Text className="text-sm text-gray-600 dark:text-gray-400 mr-2">$</Text>
        <TextInput
          value={value}
          onChangeText={onChange}
          editable={!readOnly}
          keyboardType="numbers-and-punctuation"
          placeholder={String(holding.currentPrice.toFixed(2))}
          placeholderTextColor="#9ca3af"
          className="flex-1 border border-gray-300 dark:border-gray-700 rounded-xl px-4 py-3 text-gray-800 dark:text-gray-200"
        />
      </View>
    </View>
  );
}

interface AddedIncomeRowProps {
  draft: Draft;
  onChange: (
    patch: Partial<
      Pick<Draft, 'addedIncomeName' | 'addedIncomeMonthlyAmount' | 'addedIncomeType'>
    >,
  ) => void;
  readOnly?: boolean;
}

function AddedIncomeRow({ draft, onChange, readOnly }: AddedIncomeRowProps) {
  return (
    <View className="mb-3">
      <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Add an income source
      </Text>
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        A new stream that exists only in this scenario (e.g. side gig, future
        job). Defaults: starts now, never ends. Leave name blank for no extra
        income.
      </Text>
      <TextInput
        value={draft.addedIncomeName}
        onChangeText={(v) => onChange({ addedIncomeName: v })}
        editable={!readOnly}
        placeholder="e.g. Consulting"
        placeholderTextColor="#9ca3af"
        maxLength={120}
        className="border border-gray-300 dark:border-gray-700 rounded-xl px-4 py-3 mb-2 text-gray-800 dark:text-gray-200"
      />
      <View className="flex-row items-center gap-2">
        <View className="flex-row items-center flex-1">
          <Text className="text-sm text-gray-600 dark:text-gray-400 mr-2">$</Text>
          <TextInput
            value={draft.addedIncomeMonthlyAmount}
            onChangeText={(v) => onChange({ addedIncomeMonthlyAmount: v })}
            editable={!readOnly}
            keyboardType="numbers-and-punctuation"
            placeholder="0"
            placeholderTextColor="#9ca3af"
            className="flex-1 border border-gray-300 dark:border-gray-700 rounded-xl px-4 py-3 text-gray-800 dark:text-gray-200"
          />
          <Text className="text-sm text-gray-600 dark:text-gray-400 ml-2">/mo</Text>
        </View>
      </View>
      <View className="mt-2">
        <Select
          value={draft.addedIncomeType}
          options={INCOME_TYPE_OPTIONS}
          onChange={(v) => onChange({ addedIncomeType: v })}
          disabled={readOnly}
          size="sm"
          modalTitle="Income type"
        />
      </View>
    </View>
  );
}

interface WorkBreakRowsProps {
  streams: ScenarioIncomeStream[];
  pauses: DraftIncomeStreamPause[];
  droppedIds: string[];
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<DraftIncomeStreamPause>) => void;
  onRemove: (id: string) => void;
  readOnly?: boolean;
}

function WorkBreakRows({
  streams,
  pauses,
  droppedIds,
  onAdd,
  onUpdate,
  onRemove,
  readOnly,
}: WorkBreakRowsProps) {
  const droppedSet = new Set(droppedIds);
  const activeStreams = streams.filter((stream) => !droppedSet.has(stream.id));
  const options = activeStreams.map((stream) => ({
    value: stream.id,
    label:
      stream.annualAmount > 0
        ? `${stream.name} ($${Math.round(stream.annualAmount).toLocaleString()}/yr)`
        : stream.name,
  }));
  const canAdd = !readOnly && activeStreams.length > 0;

  return (
    <View className="mb-3 mt-1">
      <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Take a work break
      </Text>
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Pause one income stream for a defined break, then resume it in a later
        year.
      </Text>
      <View className="gap-2">
        {pauses.map((pause) => {
          const selectedStillAvailable = activeStreams.some(
            (stream) => stream.id === pause.incomeStreamId,
          );
          return (
            <View
              key={pause.id}
              className="rounded-xl border border-gray-200 dark:border-gray-700 p-3"
            >
              <Select
                value={selectedStillAvailable ? pause.incomeStreamId : null}
                options={options}
                onChange={(incomeStreamId) =>
                  onUpdate(pause.id, { incomeStreamId })
                }
                placeholder="Income stream"
                disabled={readOnly || options.length === 0}
                size="sm"
                modalTitle="Income stream"
              />
              <View className="flex-row gap-2 mt-2">
                <View className="flex-1">
                  <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Break starts
                  </Text>
                  <TextInput
                    value={pause.startYear}
                    onChangeText={(startYear) =>
                      onUpdate(pause.id, { startYear })
                    }
                    editable={!readOnly}
                    keyboardType="number-pad"
                    maxLength={4}
                    placeholder="2028"
                    placeholderTextColor="#9ca3af"
                    className="border border-gray-300 dark:border-gray-700 rounded-xl px-4 py-3 text-gray-800 dark:text-gray-200"
                  />
                </View>
                <View className="flex-1">
                  <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Work resumes
                  </Text>
                  <TextInput
                    value={pause.resumeYear}
                    onChangeText={(resumeYear) =>
                      onUpdate(pause.id, { resumeYear })
                    }
                    editable={!readOnly}
                    keyboardType="number-pad"
                    maxLength={4}
                    placeholder="2030"
                    placeholderTextColor="#9ca3af"
                    className="border border-gray-300 dark:border-gray-700 rounded-xl px-4 py-3 text-gray-800 dark:text-gray-200"
                  />
                </View>
              </View>
              {!readOnly && (
                <Pressable
                  onPress={() => onRemove(pause.id)}
                  className="self-start mt-2"
                  accessibilityRole="button"
                >
                  <Text className="text-xs font-semibold text-red-600 dark:text-red-400">
                    Remove work break
                  </Text>
                </Pressable>
              )}
            </View>
          );
        })}
        {!readOnly && (
          <Pressable
            onPress={canAdd ? onAdd : undefined}
            disabled={!canAdd}
            className={
              'rounded-xl border border-dashed px-4 py-3 flex-row items-center justify-center ' +
              (canAdd
                ? 'border-gray-300 dark:border-gray-700'
                : 'border-gray-200 dark:border-gray-800 opacity-60')
            }
            accessibilityRole="button"
          >
            <Ionicons
              name="add-circle-outline"
              size={16}
              color={canAdd ? '#16a34a' : '#9ca3af'}
            />
            <Text
              className={
                'text-sm font-semibold ml-2 ' +
                (canAdd
                  ? 'text-gray-700 dark:text-gray-300'
                  : 'text-gray-400 dark:text-gray-500')
              }
            >
              {canAdd ? 'Add a work break' : 'Add a wage income stream first'}
            </Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

interface WageStreamTogglesProps {
  streams: ScenarioIncomeStream[];
  droppedIds: string[];
  onToggle: (id: string) => void;
  readOnly?: boolean;
}

function WageStreamToggles({ streams, droppedIds, onToggle, readOnly }: WageStreamTogglesProps) {
  if (streams.length === 0) return null;
  const droppedSet = new Set(droppedIds);
  return (
    <View className="mb-3 mt-1">
      <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Leave specific jobs
      </Text>
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Simulate getting laid off or quitting — checked jobs are removed from the scenario.
      </Text>
      <View className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {streams.map((stream, idx) => {
          const dropped = droppedSet.has(stream.id);
          const annual = Number(stream.annualAmount ?? 0);
          return (
            <Pressable
              key={stream.id}
              onPress={() => !readOnly && onToggle(stream.id)}
              disabled={readOnly}
              className={
                'flex-row items-center px-4 py-3 ' +
                (idx > 0 ? 'border-t border-gray-200 dark:border-gray-700 ' : '') +
                (dropped ? 'bg-red-50 dark:bg-red-900/20' : '')
              }
            >
              <View className="flex-1 pr-3">
                <Text
                  className={
                    'text-sm font-medium ' +
                    (dropped
                      ? 'text-gray-500 dark:text-gray-400 line-through'
                      : 'text-gray-800 dark:text-gray-200')
                  }
                >
                  {stream.name}
                </Text>
                {annual > 0 && (
                  <Text className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {`$${Math.round(annual).toLocaleString()}/yr`}
                  </Text>
                )}
              </View>
              <View
                className={
                  'w-5 h-5 rounded border-2 items-center justify-center ' +
                  (dropped
                    ? 'bg-red-600 border-red-600'
                    : 'border-gray-300 dark:border-gray-600')
                }
              >
                {dropped && (
                  <Ionicons name="checkmark" size={14} color="#ffffff" />
                )}
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

interface PercentFieldProps {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}

function PercentField({ label, hint, value, onChange, readOnly }: PercentFieldProps) {
  return (
    <View className="mb-3">
      <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </Text>
      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">{hint}</Text>
      <View className="flex-row items-center">
        <TextInput
          value={value}
          onChangeText={onChange}
          editable={!readOnly}
          keyboardType="numbers-and-punctuation"
          placeholder="e.g. -5 or 10"
          placeholderTextColor="#9ca3af"
          className="flex-1 border border-gray-300 dark:border-gray-700 rounded-xl px-4 py-3 text-gray-800 dark:text-gray-200"
        />
        <Text className="text-sm text-gray-600 dark:text-gray-400 ml-2">%</Text>
      </View>
    </View>
  );
}
